import React from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import { FormControlLabel, Switch } from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";
import { DeliveryTimeSlot } from "@types";
import { ORDER_STATUS } from "utils/constants";

interface TimeSlotItemProps {
  selected: boolean;
  slotKey: string;
  onSelectTimeSlot: (key: string) => void;
  label: string;
  icon?: React.ReactNode;
}

interface TimeSlotSelectorProps {
  selectedTimeSlot: string | null;
  handleSelectTimeSlot: (timeSlot: string) => void;
  noSuitableTimeSlot: boolean;
  toggleNoSuitableTimeSlot: VoidFunction;
  availableTimeSlotsForDate: DeliveryTimeSlot[];
  loading: boolean;
  handleSubmit: () => void;
  confirmedDateSameAsSelected: boolean;
  status: string;
  isNoSuitableTimeslotAvailable: boolean;
}

const MoonIcon = () => (
  <svg
    className="time-slot-item__moon-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ color: "#6b46c1" }}
  >
    <path d="M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 0 1-4.4 2.26 5.403 5.403 0 0 1-3.14-9.8c-.44-.06-.9-.1-1.36-.1z" />
  </svg>
);

const NextDayIcon = () => (
  <svg
    className="time-slot-item__next-day-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ color: "#38a169" }}
  >
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
  </svg>
);

const TimeSlotItem = React.memo(
  ({ selected, slotKey, onSelectTimeSlot, label, icon }: TimeSlotItemProps) => {
    const handleClick = React.useCallback(() => {
      onSelectTimeSlot(slotKey);
    }, [onSelectTimeSlot, slotKey]);

    return (
      <div
        className={classNames("time-slot-item", {
          "time-slot-item--selected": selected,
        })}
        onClick={handleClick}
      >
        <span>{label}</span>
        {icon}
      </div>
    );
  },
);

const TimeSlotSelector = ({
  selectedTimeSlot,
  handleSelectTimeSlot,
  noSuitableTimeSlot,
  toggleNoSuitableTimeSlot,
  availableTimeSlotsForDate,
  loading,
  handleSubmit,
  confirmedDateSameAsSelected,
  status,
  isNoSuitableTimeslotAvailable = true,
}: TimeSlotSelectorProps) => {
  const hasTimeSlotAndSuitable = React.useMemo(() => {
    const hasAvailableTimeSlotsForDate = availableTimeSlotsForDate.length >= 1;

    return hasAvailableTimeSlotsForDate && !noSuitableTimeSlot;
  }, [availableTimeSlotsForDate, noSuitableTimeSlot]);

  const isLoadingButtonDisabled = React.useMemo<boolean>(() => {
    if (status === ORDER_STATUS.WAITING_FOR_CUSTOMER && noSuitableTimeSlot) {
      return false;
    }

    return (
      (!selectedTimeSlot &&
        !noSuitableTimeSlot &&
        status !== ORDER_STATUS.PENDING) ||
      confirmedDateSameAsSelected ||
      (status === ORDER_STATUS.PENDING && !selectedTimeSlot)
    );
  }, [
    selectedTimeSlot,
    noSuitableTimeSlot,
    confirmedDateSameAsSelected,
    status,
  ]);

  const { normalSlots, afterworkSlots, nextDaySlots } = React.useMemo(() => {
    const normal: DeliveryTimeSlot[] = [];
    const afterwork: DeliveryTimeSlot[] = [];
    const nextDay: DeliveryTimeSlot[] = [];
    for (const slot of availableTimeSlotsForDate) {
      if (slot.is_after_work) {
        afterwork.push(slot);
      } else if (slot.is_next_day) {
        nextDay.push(slot);
      } else {
        normal.push(slot);
      }
    }
    return {
      normalSlots: normal,
      afterworkSlots: afterwork,
      nextDaySlots: nextDay,
    };
  }, [availableTimeSlotsForDate]);

  const renderSlotItem = (
    timeSlot: DeliveryTimeSlot,
    icon?: React.ReactNode,
  ) => {
    const key = timeSlot.key;
    const from = dayjs(timeSlot.date + "T" + timeSlot.hour_from).format(
      "HH:mm",
    );
    const to = dayjs(timeSlot.date + "T" + timeSlot.hour_to).format("HH:mm");
    return (
      <TimeSlotItem
        key={key}
        selected={key === selectedTimeSlot}
        slotKey={key}
        onSelectTimeSlot={handleSelectTimeSlot}
        label={`Entre ${from} et ${to}`}
        icon={icon}
      />
    );
  };

  return (
    <div className="time-slot-selector">
      {hasTimeSlotAndSuitable && (
        <div className="time-slot-selector__title">Choisissez un créneau:</div>
      )}
      <div className="time-slot-selector__time-slots">
        {!noSuitableTimeSlot && (
          <>
            {/* Normal slots */}
            {normalSlots.map((slot) => renderSlotItem(slot))}

            {/* Afterwork section */}
            {afterworkSlots.length > 0 && (
              <div className="time-slot-section time-slot-section--afterwork">
                <div className="time-slot-section__header">
                  <MoonIcon />
                  <span className="time-slot-section__title">Afterwork</span>
                </div>
                {afterworkSlots.map((slot) =>
                  renderSlotItem(slot, <MoonIcon />),
                )}
              </div>
            )}

            {/* J+1 section */}
            {nextDaySlots.length > 0 && (
              <div className="time-slot-section time-slot-section--next-day">
                <div className="time-slot-section__header">
                  <NextDayIcon />
                  <span className="time-slot-section__title">J+1</span>
                </div>
                {nextDaySlots.map((slot) =>
                  renderSlotItem(slot, <NextDayIcon />),
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="time-slot-selector__submit">
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          size="large"
          disabled={isLoadingButtonDisabled}
        >
          Valider
        </LoadingButton>
      </div>
      {isNoSuitableTimeslotAvailable && (
        <div className="time-slot-selector__no-suitable-slots">
          <FormControlLabel
            control={
              <Switch
                checked={noSuitableTimeSlot}
                onChange={toggleNoSuitableTimeSlot}
              />
            }
            label="Merci de me contacter car je ne trouve pas de créneau disponible ou qui me convient"
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(TimeSlotSelector);
